// Role-based access control utilities

// Role definitions
export const ROLES = {
  USER: "user",
  ADMIN: "admin",
  SUPER_DEVELOPER: "super_developer",
  DEVELOPER: "developer",
  SHOP_ADMIN: "shop_admin",
  ORGANIZATION_ADMIN: "organization_admin",
  VIEWER: "viewer",
  MERCHANT: "merchant",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// Permission levels (hierarchical)
export const PERMISSION_LEVELS = {
  [ROLES.SUPER_DEVELOPER]: 7,
  [ROLES.DEVELOPER]: 6,
  [ROLES.ORGANIZATION_ADMIN]: 5,
  [ROLES.ADMIN]: 4,
  [ROLES.MERCHANT]: 4,
  [ROLES.SHOP_ADMIN]: 3,
  [ROLES.VIEWER]: 2,
  [ROLES.USER]: 1,
} as const;

// Route permissions mapping
export const ROUTE_PERMISSIONS: Record<string, Role[]> = {
  // Public routes (no authentication required)
  "/login": [],
  "/register": [],
  "/verify-otp": [],
  "/service": [],

  // User app routes (under app/(main)/ – no /main in URL)
  "/home": [ROLES.USER],
  "/profile": [
    ROLES.USER,
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.VIEWER,
  ],
  "/orders": [
    ROLES.USER,
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.VIEWER,
  ],
  "/scan": [ROLES.USER],
  "/services": [ROLES.USER],
  "/store-details": [ROLES.USER],
  "/nearby-location": [ROLES.USER],
  "/confirm-order": [ROLES.USER],
  "/coupons": [ROLES.USER],
  "/equipment-status": [ROLES.USER],
  "/report": [ROLES.USER],
  "/equipment-repaire": [ROLES.USER],
  "/notifications": [ROLES.USER],
  "/help": [ROLES.USER],
  "/about": [ROLES.USER],
  // User app settings (same path segment as admin; USER can access (main)/settings)
  "/settings": [
    ROLES.USER,
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
    ROLES.ORGANIZATION_ADMIN,
  ],

  // Admin routes
  "/dashboard": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.SHOP_ADMIN,
    ROLES.VIEWER,
  ],
  "/users": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
  ],
  "/shops": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.SHOP_ADMIN,
  ],
  "/organization": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
  ],
  "/reports": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.SHOP_ADMIN,
    ROLES.VIEWER,
  ],
  "/issue": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.SHOP_ADMIN,
  ],
  "/shop": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
  ],
  "/machine": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
  ],
  "/transaction": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.SHOP_ADMIN,
  ],
  "/manage-coupons": [ROLES.ADMIN],
  "/manage-request-refund": [ROLES.ADMIN],
  "/support": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
  ],
  "/logs": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.VIEWER,
  ],
  "/tracking": [
    ROLES.ADMIN,
    ROLES.SUPER_DEVELOPER,
    ROLES.DEVELOPER,
    ROLES.ORGANIZATION_ADMIN,
  ],

  // Developer routes
  "/developer": [ROLES.SUPER_DEVELOPER, ROLES.DEVELOPER],
  "/developer/api": [ROLES.SUPER_DEVELOPER, ROLES.DEVELOPER],
  "/developer/logs": [ROLES.SUPER_DEVELOPER, ROLES.DEVELOPER],

  // Super developer routes
  "/system": [ROLES.SUPER_DEVELOPER],
  "/system/config": [ROLES.SUPER_DEVELOPER],
  "/system/maintenance": [ROLES.SUPER_DEVELOPER],

  // Merchant routes
  "/merchant/dashboard": [ROLES.MERCHANT],
  "/merchant/shops": [ROLES.MERCHANT],
  "/merchant/machines": [ROLES.MERCHANT],
  "/merchant/machines/add": [ROLES.MERCHANT],
  "/merchant/monitoring": [ROLES.MERCHANT],
  "/merchant/statistics": [ROLES.MERCHANT],
  "/merchant/transactions": [ROLES.MERCHANT],
  "/merchant/my": [ROLES.MERCHANT],
};

// Default redirects based on role
export const DEFAULT_REDIRECT_BY_ROLE: Record<Role, string> = {
  [ROLES.USER]: "/home",
  [ROLES.ADMIN]: "/dashboard",
  [ROLES.SUPER_DEVELOPER]: "/dashboard",
  [ROLES.DEVELOPER]: "/dashboard",
  [ROLES.SHOP_ADMIN]: "/dashboard",
  [ROLES.ORGANIZATION_ADMIN]: "/dashboard",
  [ROLES.VIEWER]: "/dashboard",
  [ROLES.MERCHANT]: "/merchant/dashboard",
};

// Authentication functions
export const isAuthenticated = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem("isAuthenticated") === "true";
  } catch {
    return false;
  }
};

export const getToken = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("token");
  } catch {
    return null;
  }
};

export const getUserRole = (): Role | null => {
  if (typeof window === "undefined") return null;
  try {
    const role = localStorage.getItem("userRole");
    return role as Role | null;
  } catch {
    return null;
  }
};

export const getCurrentUser = (): any | null => {
  if (typeof window === "undefined") return null;
  try {
    const user = localStorage.getItem("currentUser");
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

// Permission checking functions
export const hasPermission = (
  userRole: Role | null,
  requiredRoles: Role[],
): boolean => {
  if (!userRole) return false;
  if (requiredRoles.length === 0) return true; // Public route
  return requiredRoles.includes(userRole);
};

export const hasPermissionLevel = (
  userRole: Role | null,
  minimumRole: Role,
): boolean => {
  if (!userRole) return false;
  const userLevel = PERMISSION_LEVELS[userRole] || 0;
  const requiredLevel = PERMISSION_LEVELS[minimumRole] || 0;
  return userLevel >= requiredLevel;
};

// Route permission checking
export const canAccessRoute = (pathname: string): boolean => {
  const userRole = getUserRole();

  // Public routes (login, register, etc.)
  const publicRoutes = ["/login", "/register", "/verify-otp", "/service", "/"];
  if (
    publicRoutes.some(
      (route) => pathname === route || pathname.startsWith(route + "/"),
    )
  ) {
    return true;
  }

  // Check if user is authenticated
  if (!isAuthenticated()) {
    return false;
  }

  // Find matching route permission
  let matchedRoute: string | null = null;
  let matchedRoles: Role[] = [];

  // Check exact match first
  if (ROUTE_PERMISSIONS[pathname]) {
    matchedRoute = pathname;
    matchedRoles = ROUTE_PERMISSIONS[pathname];
  } else {
    // Check for partial matches (e.g., /settings/profile matches /settings)
    const sortedRoutes = Object.keys(ROUTE_PERMISSIONS).sort(
      (a, b) => b.length - a.length,
    );

    for (const route of sortedRoutes) {
      if (pathname.startsWith(route)) {
        matchedRoute = route;
        matchedRoles = ROUTE_PERMISSIONS[route];
        break;
      }
    }
  }

  // If no route permission defined, default to requiring authentication only
  if (!matchedRoute) {
    return isAuthenticated();
  }

  // Check if user has permission
  return hasPermission(userRole, matchedRoles);
};

// Get default redirect for user
export const getDefaultRedirect = (userRole?: Role | null): string => {
  const role = userRole || getUserRole();
  return role ? DEFAULT_REDIRECT_BY_ROLE[role] : "/login";
};

// Helper function to notify components when user role changes
export const notifyRoleChange = (): void => {
  if (typeof window === "undefined") return;
  try {
    // Dispatch custom event to notify components of role change
    window.dispatchEvent(new Event("userRoleChanged"));
  } catch (error) {
    console.error("Error notifying role change:", error);
  }
};

// Set user role and notify components
export const setUserRole = (role: Role): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("userRole", role);
    notifyRoleChange();
  } catch (error) {
    console.error("Error setting user role:", error);
  }
};

// Sync user role from backend user data - automatically updates localStorage if role changed
export const syncUserRoleFromData = (userData: any): void => {
  if (typeof window === "undefined") return;
  if (!userData) return;

  try {
    const backendRole = userData.role;
    if (!backendRole) return;

    const currentRole = getUserRole();

    // If role changed in backend, update localStorage immediately
    if (backendRole !== currentRole) {
      setUserRole(backendRole);
    }
  } catch (error) {
    console.error("Error syncing user role from data:", error);
  }
};

// Logout function
export const logout = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem("isAuthenticated"); // this not important just for testing
    localStorage.removeItem("userRole");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("token");
    sessionStorage.clear();
    notifyRoleChange(); // Notify components that role has been cleared
  } catch (error) {
    console.error("Error during logout:", error);
  }
};

// Check if user has any of the specified roles
export const hasAnyRole = (roles: Role[]): boolean => {
  const userRole = getUserRole();
  return userRole ? roles.includes(userRole) : false;
};

// Check if user has all of the specified roles (useful for combined permissions)
export const hasAllRoles = (roles: Role[]): boolean => {
  const userRole = getUserRole();
  if (!userRole) return false;
  // This is simplified - in a real app you might have multiple roles per user
  return roles.every((role) => role === userRole);
};

// Get user permission level
export const getUserPermissionLevel = (): number => {
  const userRole = getUserRole();
  return userRole ? PERMISSION_LEVELS[userRole] || 0 : 0;
};

// Check if user is admin or higher
export const isAdmin = (): boolean => {
  return hasPermissionLevel(getUserRole(), ROLES.ADMIN);
};

// Check if user is developer or higher
export const isDeveloper = (): boolean => {
  return hasPermissionLevel(getUserRole(), ROLES.DEVELOPER);
};

// Check if user is super developer
export const isSuperDeveloper = (): boolean => {
  return getUserRole() === ROLES.SUPER_DEVELOPER;
};

// Navigation items structure
export interface NavigationItem {
  title: string;
  url: string;
  icon: string;
  allowedRoles: Role[];
  mobileTab?: boolean; // Show in mobile bottom navigation
}

// Navigation items with role permissions
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: "LayoutDashboard",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
      ROLES.VIEWER,
      ROLES.SHOP_ADMIN,
    ],
    mobileTab: true,
  },
  {
    title: "Organizations",
    url: "/organization",
    icon: "Building2",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
    ],
    mobileTab: false,
  },
  {
    title: "Users",
    url: "/users",
    icon: "Users",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
    ],
    mobileTab: false,
  },
  {
    title: "Shops",
    url: "/shop",
    icon: "Store",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.SHOP_ADMIN,
    ],
    mobileTab: true,
  },
  {
    title: "Machines",
    url: "/machine",
    icon: "WashingMachine",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.SHOP_ADMIN,
    ],
  },
  {
    title: "Transactions",
    url: "/transaction",
    icon: "CreditCard",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.SHOP_ADMIN,
    ],
    mobileTab: false,
  },
  {
    title: "Manage Coupons",
    url: "/manage-coupons",
    icon: "Tickets",
    allowedRoles: [ROLES.ADMIN],
    mobileTab: false,
  },
  {
    title: "Refund requests",
    url: "/manage-request-refund",
    icon: "Undo2",
    allowedRoles: [ROLES.ADMIN],
    mobileTab: false,
  },
  {
    title: "Support",
    url: "/support",
    icon: "HeadphonesIcon",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
    ],
    mobileTab: false,
  },
  {
    title: "Issue Management",
    url: "/issue",
    icon: "AlertCircle",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
      ROLES.SHOP_ADMIN,
    ],
    mobileTab: false,
  },
  {
    title: "Logs",
    url: "/logs",
    icon: "FileText",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
      ROLES.VIEWER,
    ],
    mobileTab: true,
  },
  {
    title: "Tracking",
    url: "/tracking",
    icon: "MapPin",
    allowedRoles: [
      ROLES.ADMIN,
      ROLES.SUPER_DEVELOPER,
      ROLES.DEVELOPER,
      ROLES.ORGANIZATION_ADMIN,
    ],
    mobileTab: false,
  },
  // {
  //   title: 'Settings',
  //   url: '/setting',
  //   icon: 'Settings',
  //   allowedRoles: [
  //     ROLES.ADMIN,
  //     ROLES.SUPER_DEVELOPER,
  //     ROLES.DEVELOPER,
  //     ROLES.ORGANIZATION_ADMIN,
  //     ROLES.SHOP_ADMIN,
  //   ],
  //   mobileTab: true,
  // },
  // Merchant navigation items (desktop sidebar)
  {
    title: "Dashboard",
    url: "/merchant/dashboard",
    icon: "LayoutDashboard",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: true,
  },
  {
    title: "Shops",
    url: "/merchant/shops",
    icon: "Store",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: true,
  },
  {
    title: "Machines",
    url: "/merchant/machines",
    icon: "WashingMachine",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: true,
  },
  {
    title: "Monitoring",
    url: "/merchant/monitoring",
    icon: "Activity",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: false,
  },
  {
    title: "Statistics",
    url: "/merchant/statistics",
    icon: "BarChart3",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: false,
  },
  {
    title: "Transactions",
    url: "/merchant/transactions",
    icon: "CreditCard",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: false,
  },
  {
    title: "My",
    url: "/merchant/my",
    icon: "User",
    allowedRoles: [ROLES.MERCHANT],
    mobileTab: true,
  },
];

// Get filtered navigation items based on user role
export const getFilteredNavigationItems = (
  userRole: Role | null,
): NavigationItem[] => {
  if (!userRole) return [];

  return NAVIGATION_ITEMS.filter((item) =>
    hasPermission(userRole, item.allowedRoles),
  );
};
